import { type ApiSchemas, call, newIdempotencyKey } from '@rental/contracts/client'
import { format, formatDateTime } from '@rental/i18n'
import { Notice } from '@rental/ui'
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useLocale } from '../i18n'
import type { DragProposal } from './timeline-drag'
import {
  type BoardCache,
  commitMove,
  proposeMove,
  type ReschedulePatch,
  type RescheduleReason,
} from './timeline-reschedule'

type Category = ApiSchemas['TimelineResponse']['categories'][number]
type Board = ApiSchemas['TimelineResponse']
type Message = { tone: 'success' | 'danger'; reason: RescheduleReason | 'moved' }

/** The generated client's PATCH, in the shape the commit logic is written against. */
export const patchSchedule: ReschedulePatch = ({ code, body, dryRun, key }) =>
  call(
    api.PATCH('/v1/staff/bookings/{code}/schedule', {
      params: {
        path: { code },
        header: { 'idempotency-key': key },
        ...(dryRun ? { query: { dryRun: '1' as const } } : {}),
      },
      body,
    }),
  )

/**
 * Wires a finished drag to the API for one category: a dry run first (a
 * refusal is explained at once), then an in-app confirm, then the real move
 * with the bar shifted in the cache straight away and put back on failure.
 */
export function useReschedule(
  category: Category,
  boardKey: readonly unknown[],
): { onProposal: (proposal: DragProposal) => void; ui: ReactNode } {
  const { locale, messages: m } = useLocale()
  const queryClient = useQueryClient()
  const dialog = useRef<HTMLDialogElement | null>(null)
  const [pending, setPending] = useState<DragProposal | null>(null)
  const [busy, setBusy] = useState(false)
  /** Which stage the one allowed reschedule flow is in; a new drag is ignored unless idle. */
  const phase = useRef<'idle' | 'proposing' | 'confirming' | 'committing'>('idle')
  const [message, setMessage] = useState<Message | null>(null)

  const cache = useMemo<BoardCache>(
    () => ({
      snapshot: () => queryClient.getQueryData<Board>(boardKey),
      update: (change) => queryClient.setQueryData<Board>(boardKey, (b) => (b ? change(b) : b)),
      restore: (snapshot) => queryClient.setQueryData<Board>(boardKey, snapshot),
      invalidate: () => queryClient.invalidateQueries({ queryKey: ['timeline'] }),
    }),
    [queryClient, boardKey],
  )

  const onProposal = useCallback((proposal: DragProposal) => {
    if (phase.current !== 'idle') return
    phase.current = 'proposing'
    setMessage(null)
    proposeMove(patchSchedule, proposal, newIdempotencyKey)
      .then((outcome) => {
        if (outcome.kind === 'refused') {
          phase.current = 'idle'
          setMessage({ tone: 'danger', reason: outcome.reason })
        } else {
          phase.current = 'confirming'
          setPending(proposal)
        }
      })
      .catch(() => {
        phase.current = 'idle'
        setMessage({ tone: 'danger', reason: 'moveFailed' })
      })
  }, [])

  const closeDialog = useCallback(() => {
    // Closing after Confirm is part of the commit; only a cancel frees the flow.
    if (phase.current === 'confirming') phase.current = 'idle'
    setPending(null)
  }, [])

  useEffect(() => {
    if (pending && !dialog.current?.open) dialog.current?.showModal()
  }, [pending])

  async function confirm() {
    if (!pending) return
    const proposal = pending
    phase.current = 'committing'
    dialog.current?.close()
    setBusy(true)
    let outcome: Awaited<ReturnType<typeof commitMove>>
    try {
      await queryClient.cancelQueries({ queryKey: ['timeline'] })
      outcome = await commitMove(patchSchedule, cache, proposal, newIdempotencyKey)
    } catch {
      outcome = { kind: 'failed', reason: 'moveFailed' }
      await queryClient.invalidateQueries({ queryKey: ['timeline'] })
    }
    setBusy(false)
    phase.current = 'idle'
    setMessage(
      outcome.kind === 'moved'
        ? { tone: 'success', reason: 'moved' }
        : { tone: 'danger', reason: outcome.reason },
    )
  }

  const plate = (id: string | null) =>
    category.vehicles.find((vehicle) => vehicle.id === id)?.plate ?? m.timeline.unassigned
  const current = pending
    ? [...category.vehicles.flatMap((v) => v.items), ...category.unassigned].find(
        (item) => item.booking?.code === pending.code && item.kind === 'reservation',
      )
    : undefined
  const range = (from: string | Date, to: string | Date | null) =>
    `${formatDateTime(locale, new Date(from).toISOString())} – ${to ? formatDateTime(locale, new Date(to).toISOString()) : ''}`

  const ui = (
    <>
      {message ? (
        <Notice tone={message.tone} live={message.tone === 'danger' ? 'assertive' : 'polite'}>
          <p>{m.timeline[message.reason]}</p>
        </Notice>
      ) : null}
      <ConfirmDialog
        dialogRef={dialog}
        title={m.timeline.confirmMoveTitle}
        confirmLabel={m.timeline.confirmMove}
        cancelLabel={m.timeline.cancelMove}
        busy={busy}
        onConfirm={() => void confirm()}
        onClose={closeDialog}
      >
        {pending ? (
          <p>
            {format(m.timeline.confirmMoveBody, {
              code: pending.code,
              vehicle:
                pending.fromVehicleId === pending.toVehicleId
                  ? plate(pending.toVehicleId)
                  : `${plate(pending.fromVehicleId)} → ${plate(pending.toVehicleId)}`,
              from: current ? range(current.startsAt, current.endsAt) : '',
              to: range(pending.startsAt, pending.endsAt),
            })}
          </p>
        ) : null}
      </ConfirmDialog>
    </>
  )
  return { onProposal, ui }
}
